<template>
  <section
    :class="['poll-wrap', brandClass, { loaded, hide: shouldHide, isHorizontal: WEBIsHorizontal, headerEnabled }]"
    :style="{
      fontFamily: fontFamilyCSS
    }">
    <BrandingHeader v-if="showBranding" />
    <AuthHeader v-if="showAuthHeader && isAuthenticated" />
    <transition name="navheader">
      <NavHeader v-if="headerEnabled" />
    </transition>
    <SignUp v-if="showSignup && !isLoading" />
    <Congratulations v-if="view === 'congratulations' && pollGroupModeActive" />
    <Leaderboard v-if="view === 'leaderboard' && leaderboardEnabled" />
    <Summary v-if="view === 'summary'" />
    <PollGroups v-if="view === 'pollGroups' && !showSignup" />
    <Winner v-if="view === 'winner'" />
    <div class="swpoll-content" v-if="['poll', 'correctAnswer'].includes(view) && !stayTunedVisible && !showSignup">
      <Admin v-if="showAdmin" />
      <transition name="preloader">
        <Preloader v-if="!loaded" />
      </transition>
      <Background />
      <div v-if="voteLimitReached" class="vote-limit-reached">
        You have already voted!
      </div>
      <transition name="block-1">
        <Timer v-if="loaded && componentsLoaded && (showTimer || showThankYou)" />
      </transition>
      <transition name="block-2">
        <HeaderImage v-if="loaded && WEBHeaderImageURI && WEBHeaderImageURI !== 'none'" />
      </transition>
      <transition name="block-3">
        <Question v-if="loaded" />
      </transition>
      <Options
        v-if="!(!isVoteable && WEBOnPollClosing === 'tuned') && loaded && pollType === 'poll' && !isNextPollLoading"
        @on-options-loaded="setPollLoaded(true)" :key="`poll-${pollId}`" />
      <Feedback v-if="!(!isVoteable && WEBOnPollClosing === 'tuned') && loaded && isFeedbackPoll"
        @on-feedback-loaded="setPollLoaded(true)" :key="`feedback-${pollId}`" />
      <transition name="block-5">
        <PoweredBy v-if="loaded" data-iframe-height="1" />
      </transition>
      <div class="page-end-marker" data-iframe-height="1">.</div>
      <OptionActionModal></OptionActionModal>
    </div>
    <StayTuned v-if="stayTunedVisible && loaded && !isLoading" />
    <Unauthorized v-if="view === 'unauthorized'" />
    <notifications group="general" position="top right" />
  </section>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapActions,
  mapMutations
} from 'vuex';

import Admin from './components/Admin';
import NavHeader from './components/NavHeader';
import BrandingHeader from './components/BrandingHeader';
import Preloader from './components/Preloader';
import Background from './components/Background';
import Timer from './components/Timer';
import HeaderImage from './components/HeaderImage';
import Question from './components/Question';
import Options from './components/Options';
import Feedback from './components/Feedback';
import AuthHeader from './components/AuthHeader';
import Leaderboard from './components/LeaderBoard';
import Summary from './components/Summary';
import Winner from './components/Winner';
import PollGroups from './components/PollGroups';
import OptionActionModal from './components/OptionActionModal';
import Vue from 'vue';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import Congratulations from './components/Congratulations';
import PoweredBy from './components/PoweredBy.vue';

export default {
  name: 'App',
  components: {
    Admin,
    NavHeader,
    BrandingHeader,
    Preloader,
    Background,
    Timer,
    HeaderImage,
    Question,
    Options,
    Feedback,
    AuthHeader,
    Leaderboard,
    Summary,
    PollGroups,
    Winner,
    OptionActionModal,
    Congratulations,
    StayTuned: () => import(/* webpackPrefetch: true */ './components/StayTuned'),
    SignUp: () => import(/* webpackPrefetch: true */ './components/SignUp'),
    PoweredBy,
    Unauthorized: () => import(/* webpackPrefetch: true */ './components/Unauthorized'),

  },
  data() {
    return {
      isLoading: false
    };
  },
  computed: {
    pollIdOrPollGroupIdOrIsAuthenticated() {
      return `${this.pollId}|${this.isAuthenticated}|${this.pollGroupId}`;
    },
    shouldHide() {
      return !this.isVoteable && this.WEBOnPollClosing === 'hide';
    },
    stayTunedVisible() {
      return !this.isPreview &&
        (
          (!this.isVoteable && this.WEBOnPollClosing === 'tuned') ||
          (this.isDynamicLink &&
            (this.pollGroupId && ((this.gamePollGroupIds || []).length === 0 || !(this.gamePollGroupIds || []).includes(this.pollGroupId))) ||
            !this.pollId)
        )
    },
    brandClass() {
      const queryParams = this.parseQueryParams();
      return queryParams.group ? `style-${this.group}` : '';
    },
    showSignup() {
      return !this.previewData && this.isAuthenticationEnabled && !this.isAuthenticated && this.view !== 'unauthorized';
    },
    ...mapState([
      'cdnOrigin'
    ]),
    ...mapState('poll', [
      'activeLinkParams',
      'showAdmin',
      'showTimer',
      'WEBHeaderImageURI',
      'headerImage',
      // 'showThankYou',
      'loaded',
      'componentsLoaded',
      'fontId',
      'fontProvider',
      'WEBIsHorizontal',
      'templateLinks',
      'WEBVotingEnabled',
      'WEBOnPollClosing',
      'isActive',
      'isFreeAccount',
      'group',
      'trustedDomains',
      'pollSettingsLink',
      'captchaEnabled',
      'voteLimitReached',
      'previewData',
      'pollGroupId',
      'componentsLoaded',
      'votingComplete',
      'WEBShowCorrectOption'
    ]),
    ...mapState('pollGroup', [
      'brandingName',
      'brandingImage',
      'leaderboardEnabled',
      'scoring',
    ]),
    ...mapState('voter', [
      'gameAuthMethods',
      'authType',
      'isAuthenticated',
      'showAuthHeader',
      'view',
      'showTimer',
      'externalToken',
      'gamePollGroups',
      'gamePollGroupIds',
      'email',
      'scoring',
      'isPreview',
    ]),
    ...mapGetters('poll', [
      'fontFamilyCSS',
      'accountName',
      'pollId',
      'oldPollId',
      'templateName',
      'viewType',
      'isVoteable',
      'isDynamicLink',
      'headerEnabled',
      'pollType',
      'isFeedbackPoll',
      'isPollGroupMode',
      'isTimerEnded',
      'showMultiVote'
    ]),
    ...mapGetters([
      'websocketConnected'
    ]),
    ...mapGetters('voter', [
      'isAuthenticationEnabled'
    ]),
    ...mapGetters('pollGroup', [
      'pollGroupModeActive',
      'isNextPollLoading'
    ]),
    showBranding() {
      return !this.stayTunedVisible && (this.brandingName || this.brandingImage);
    },
    showThankYou() {
      return !this.pollGroupModeActive || (
        (
          this.votingComplete
          || this.isTimerEnded
        ) && (
          this.showMultiVote
          || this.showTimer
          || this.view === 'correctAnswer'
          || this.WEBShowCorrectOption
        )
      );
    }
  },
  watch: {
    websocketConnected(connected) {
      console.log('websocketConnected', connected);
      if (connected) {
        ['dynamic', 'nextLink'].indexOf(this.viewType) !== -1 && this.accountName &&
          this.$socket.sendObj({
            action: 'join',
            groupName: `/poll:${this.accountName}`
          });
        if (this.isPollGroupMode) {
          this.$socket.sendObj({
            action: 'join',
            groupName: `/user:${this.accountName}`
          });
        } else {
          this.pollId && this.$socket.sendObj({
            action: 'join',
            groupName: `/poll:${this.pollId}`
          });
        }
      }
    },
    pollId(newPollId) {
      console.log('pollId', newPollId);
      this.setComponentsLoaded(false);
      if (!this.websocketConnected || this.oldPollId === newPollId) return;
      this.oldPollId && this.$socket.sendObj({
        action: 'leave',
        groupName: `/poll:${this.oldPollId}`
      });
      this.$socket.sendObj({
        action: 'join',
        groupName: `/poll:${newPollId}`
      });
      this.checkAuth();
    },
    async pollGroupId(pollGroupId) {
      if (pollGroupId) {
        const group = await this.getPollGroup(pollGroupId);
        this.setPollGroup(group);
        this.setAuthenticationMethods(group.authenticationMethods || {});
        await this.nextPoll();
      }
    },
    async pollIdOrPollGroupIdOrIsAuthenticated() {
      if (this.isAuthenticated && !this.pollGroupId && !this.pollId) {
        await this.checkAuthentication(this.isAuthenticated);
      }

      if (this.isAuthenticated && (this.pollGroupId || this.pollId)) {
        const voteAllowed = await this.checkWhitelistAccess(this.pollGroupId || this.pollId);

        if (!voteAllowed) {
            this.logout();
            this.switchView('unauthorized');
            return;
        }

        await this.checkAuthentication(this.isAuthenticated);
      }
    },
    templateName(name) {
      if (name !== 'nextGen') {
        let searchParams = window.location.search;
        if (!this.stayTunedVisible) {
          // Force reload the page and cloudfront cache
          const split = window.location.search.split('&');
          window.location.search.length ?
            split.push(`timestamp=${new Date().getTime()}`) :
            split[0] = `?timestamp=${new Date().getTime()}`;
          searchParams = split.join('&');
        }
        const pathname = window.location.pathname.replace(/(\/poll|\/pollGroup)/, '');
        window.location.href = `${process.env.VUE_APP_API_URL}${process.env.VUE_APP_ENVIRONMENT !== 'local' ? '/s' : ''}/play${pathname}${searchParams}`;
      }
    },
    gameAuthMethods: {
      handler() {
        if (this.gameAuthMethods.passwordFree === undefined) {
          return;
        }

        const queryParams = new URLSearchParams(window.location.search);
        const email = queryParams.get('email');
        const isValidEmail = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[a-z]{2,}$/.test(email);

        if (email && !isValidEmail || (email === this.email)) {
          // remove email from query params
          const url = new URL(window.location.href);
          url.searchParams.delete('email');
          window.history.replaceState({}, '', url);

          return;
        }

        if (!this.gameAuthMethods.passwordFree && email && isValidEmail) {
          Vue.notify({
            group: 'general',
            title: 'Failed to login',
            type: 'error',
            text: `Failed to log in with ${email} account due password free login is not enabled`,
            duration: 5000
          });
        }

        if (this.gameAuthMethods.passwordFree && email && isValidEmail) {
          this.logout();
        }
      },
      deep: true,
    },
    isAuthenticationEnabled(value) {
      if (value) {
        this.checkAuth();
      }
    }
  },
  methods: {
    ...mapActions('poll', [
      'loadFont',
      'loadPollSettings',
      'loadPollResults',
      'getGroup',
      'loadTrustedDomains',
      'loadTemplateVariables',
      'getAccountParameters',
      'checkVoteLimit',
      'checkActivePollWhitelist'
    ]),
    ...mapMutations('poll', [
      'setAccountName',
      'setComponentsLoaded',
      'setPollGroupId',
    ]),
    ...mapActions('voter', [
      'switchView',
      'checkForValidSession',
      'loadHashSettings',
      'loadHashSettingsView',
      'getExternalToken',
      'getQueryToken',
      'loginByToken',
      'loginVeevUser',
      'getGameSettings',
      'getAllPollGroups',
      'getPollGroup',
      'logout',
      'setStartTimeStamp',
      'checkWhitelistAccess',
    ]),
    ...mapActions('pollGroup', [
      'nextPoll'
    ]),
    ...mapMutations('voter', [
      'setHashSettings',
      'setStartTime',
      'setAuthorizationToken',
      'setVoterId',
      'setAuthType',
      'setGamePollGroups',
      'setAuthenticationMethods',
      'setIsOptInEnabled',
      'setOptInText'
    ]),
    ...mapMutations('pollGroup', [
      'setPollGroup'
    ]),
    parseQueryParams() {
      var queryString = window.location.href.split('?')[1] || '';
      var query = {};
      var pairs = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&');
      for (var i = 0; i < pairs.length; i++) {
        var pair = pairs[i].split('=');
        query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
      }
      return query;
    },
    async checkAuth() {
      try {
        if (this.isAuthenticated && this.pollId && !this.externalToken) {
          const voteAllowed = await this.checkWhitelistAccess(this.pollId);

          if (!voteAllowed) {
            this.switchView('unauthorized');
            return;
          }
        }

        const auth = this.isAuthenticated ||
          !(!this.skipAuthentication && !(await this.checkForValidSession(this.accountName)));
        window.parent.postMessage({
          auth
        }, '*');
        return auth;
      } catch (e) {
        Vue.notify({
          group: 'general',
          title: 'Invalid Session',
          type: 'error',
          text: e.message.replace(`${e.name}:`, ''),
          duration: 5000
        });
        this.switchView('signup');
      }
    },
    async checkAuthentication(isAuthenticated) {
      try {
        console.log('checkAuthentication', isAuthenticated, this.gamePollGroups.length, this.isPollGroupMode);
        if (isAuthenticated) {
          if (this.isGamificationEnabled) {
            this.getAccountParameters();
            if (this.pollGroupId) {
              const group = await this.getPollGroup(this.pollGroupId);
              this.setPollGroup(group);
              await this.nextPoll();
            } else if (this.gamePollGroupIds.length > 0) {
              await this.getAllPollGroups(this.accountName);
              if (this.isPollGroupMode) {
                this.switchView('pollGroups');
              }
            }
          } else {
            await this.init();
          }
        }
      } catch (e) {
        this.$notify({
          group: 'general',
          title: 'Loading failed',
          type: 'error',
          text: 'Failed to load the poll',
          duration: -1
        });
        this.logout();
        this.switchView('signup');
      }
    },
    async init() {
      try {
        const path = window.location.pathname.replace('/play', '/');
        let [, view, identifier, , pollGroupId] = path.split('/');

        await this.getGroup();
        this.loadFont({
          id: this.fontId,
          provider: this.fontProvider
        });
        if (view !== 'pollGroups') {
          const [isOptInEnabled, optInText] = await this.loadPollSettings();
          this.setIsOptInEnabled(isOptInEnabled);
          this.setOptInText(optInText);
          this.loadHashSettings();
        }
        if (this.view === 'poll') {
          window.parent.postMessage({
            pollSettingsReady: true
          }, '*');
        }
        await this.loadPollResults();
        this.viewType === 'nextPoll' && this.setStartTime(Date.now());
        this.checkVoteLimit();
      } catch (e) {
        Vue.notify({
          group: 'general',
          title: 'Setup failed',
          type: 'error',
          text: e.message.replace(`${e.name}:`, ''),
          duration: 5000
        });
      }
    },
    postMessageListener(event) {
      if (!this.trustedDomains.includes(event.origin)) {
        return;
      }
      try {
        const {
          command,
          userId,
          authorizationToken,
          authType
        } = JSON.parse(event.data);
        if (!command) return;
        switch (command) {
          case 'setAuthentication':
            this.setAuthorizationToken(authorizationToken);
            this.setVoterId(userId);
            this.setAuthType(authType);
            window.parent.postMessage({
              authenticated: true
            }, '*');
            break;
        }
      } catch (error) {
        console.log('error', error);
      }
    },
    async setPollLoaded(loaded) {
      try {
        this.setComponentsLoaded(loaded);
        if (this.showTimer) {
          await this.setStartTimeStamp();
        }
      } catch (e) {
        console.error('setPollLoaded', e);
      }
    }
  },
  async mounted() {
    try {
      this.isLoading = true;
      const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
      isIE11 && this.$notify({
        group: 'general',
        title: 'Unsupported Browser',
        type: 'error',
        text: 'For the best experience, use Chrome or Firefox',
        duration: -1
      });
      window.addEventListener('hashchange', async () => {
        this.loadHashSettings();
      });

      this.getQueryToken();

      const path = window.location.pathname.replace('/play', '/');
      let [, view, identifier, , pollGroupId] = path.split('/');

      // support old link format
      if (!['poll', 'pollGroups'].includes(view)) {
        identifier = view;
        view = 'poll';
      }

      this.getExternalToken();

      this.setAccountName(identifier);
      this.getAccountParameters();
      await this.loadTemplateVariables();

      if (this.externalToken) {
        await this.loginByToken();
      }

      !(this.previewData || {}).poll_setting && this.accountName && await this.getGameSettings(pollGroupId);
      this.captchaEnabled && Vue.use(VueReCaptcha, {
        siteKey: process.env.VUE_APP_GRECAPTCHA_CLIENT_KEY
      });

      if (view === 'pollGroups') {
        if (!pollGroupId) {
          await this.getAllPollGroups(this.accountName);

          if (this.gamePollGroupIds.length > 0) {
            this.loadFont({
              id: this.fontId,
              provider: this.fontProvider
            });
          }
          await this.loadTrustedDomains();
          addEventListener('message', (e) => this.postMessageListener(e), false);
        } else {
          view = 'poll';
          await this.setPollGroupId(pollGroupId);
          const group = await this.getPollGroup(pollGroupId);
          this.setPollGroup(group);
          this.setAuthenticationMethods(group.authenticationMethods || {});
          // await this.nextPoll();
        }
      }
      await this.init();
      this.switchView(view);
    } catch (err) {
      console.error(err);
    } finally {
      this.isLoading = false;
    }
  }
}
</script>

<style lang="scss">
:root {
  --font-body: Circular, Helvetica Neue, Helvetica, Arial, sans-serif;
  --font-head: Circular, Helvetica Neue, Helvetica, Arial, sans-serif;
  --background-img: none;
  --background-color: #3A7FCF;
  --text-color: #fff;
  --content-block-width: 460px;

  --preloader-bg: #3A7FCF;

  --padding-hor: 40px;
  --padding-vert: 20px;

  --secondary-color: #6E5ABF;

  --question-color: #fff;
  --question-fontsize: 20px;
  --question-topmmargin: 30px;
  --question-bottommargin: 30px;

  --question-image-max-height: 140px;

  --answer-color: #fff;
  --answer-gradient: linear-gradient(180deg, #2D5F9B 0%, #143162 100%);
  --answer-bg: none;
  --answer-fontsize: 18px;
  --answer-bottommargin: 10px;
  --answer-bordercolor: #fff;
  --answer-borderactivecolor: #aee5f3;
  --answer-bg-active: #ffffff81;
  --answer-bg-activeRGB: 255, 255, 255;
  --answer-active: #222222;
  --answer-borderwidth: 1px;
  --answer-borderactivewidth: 3px;
  --answer-radius: 30px;
  --answer-padding-ver: 8px;
  --answer-padding-hor: 20px;
  --answer-min-height: 50px;

  --answer-hor-padding-ver: 9px;
  --answer-hor-padding-hor: 18px;
  --answer-hor-fontsize: 15px;
  --answer-hor-min-height: inherit;
  --question-hor-fontsize: 18px;

  --answer-img-width: 40px;
  --answer-img-height: 40px;
  --answer-img-radius: 50%;

  --result-bar-bg: rgba(255, 255, 255, .3);
  --result-bar-color: #fff;
  --result-bar-fontsize: 80px;
  --result-bar-correct-bg: #3ad67f;
  --result-bar-correct-bgRGB: rgba(58, 214, 127, 0.7);
  --result-bar-correct-color: #fff;

  --form-bg: transparent;
  --form-color: rgba(0, 0, 0, .8);
  --form-placeholder-color: rgba(0, 0, 0, 0.25);
  --form-height: 50px;

  --button-radius: 30px;
  --button-bg: #3ad67f;
  --button-color: #fff;

  --success-color: #3ad67f;
  --danger-color: #ff7676;
  --white: #fff;

  --padding-hor-mob: 15px;

  .style-wes {
    --background-img: none;
    --background-color: transparent;
    --answer-bg: #2dd3fb;
    --answer-active: #222;
    --question-color: #1b223c;
  }

  .style-nobarriers {
    --background-img: none;
    --background-color: transparent;
    --text-color: #fff;
    --content-block-width: 460px;

    --preloader-bg: transparent;

    --question-color: #072B31;

    --answer-color: #fff;
    --answer-gradient: linear-gradient(180deg, #2D5F9B 0%, #143162 100%);
    --answer-bg: #072B31;
    --answer-fontsize: 18px;
    --answer-bottommargin: 10px;
    --answer-bordercolor: #fff;
    --answer-borderactivecolor: #aee5f3;
    --answer-bg-active: #FF8200;
    --answer-bg-activeRGB: 7, 43, 49;
    --answer-active: #fff;
    --answer-borderwidth: 0;
    --answer-borderactivewidth: 0;
    --answer-radius: 0;

    --result-bar-bg: rgba(7, 43, 49, .8);
    --result-bar-color: rgba(7, 43, 49, .1);
    --result-bar-fontsize: 80px;
    --result-bar-correct-bg: #3ad67f;
    --result-bar-correct-bgRGB: 58, 214, 127;
    --result-bar-correct-color: #fff;
  }

  .style-aeip {
    --background-img: none;
    --background-color: transparent;
    --text-color: rgba(0, 0, 0, .3);
    --preloader-bg: var(--background-color);
    --question-color: #1B233A;
    --answer-color: #fff;
    --answer-gradient: none;
    --answer-bg: #2B57A5;
    --answer-bordercolor: #2B57A5;
    --answer-borderactivecolor: #aee5f3;
    --answer-bg-active: #2B57A5;
    --answer-bg-activeRGB: 43, 87, 165;
    --answer-active: #fff;
    --answer-radius: 6px;
    --answer-show-bg: rgba(43, 87, 165, 0.55);
    --result-bar-bg: #a1b9e4;
    --result-bar-color: #2B57A5;
  }

  .style-covenant {
    --font-body: Open Sans, Helvetica Neue, Helvetica, Arial, sans-serif;
    --font-head: Open Sans, Helvetica Neue, Helvetica, Arial, sans-serif;
    --question-fontsize: 1.75em;
    --question-hor-fontsize: 1.75em;
    --answer-hor-fontsize: 0.9em;
    background-image: url("~@/assets/img/covenant-bg.jpg");
    background-repeat: no-repeat;
    background-size: cover;
    --background-color: #FEB813;
    --text-color: #222;
    --preloader-bg: var(--background-color);
    --question-color: #1B233A;
    --answer-color: #0082C9;
    --answer-gradient: none;
    --answer-bg: #0082C9;
    --answer-bordercolor: #0082C9;
    --answer-borderactivecolor: #aee5f3;
    --answer-bg-active: #2B57A5;
    --answer-bg-activeRGB: 43, 87, 165;
    --answer-active: #fff;
    --answer-radius: 3px;
    --answer-show-bg: rgba(0, 0, 0, .15);
    --result-bar-bg: #a1b9e4;
    --result-bar-color: #2B57A5;
    --question-fontweight: 700;
    --answer-fontweight: 700;
    --answer-hor-padding-ver: 14px;
    --answer-hor-padding-hor: 25px;
  }

  .style-cryms {
    --font-body: Oswald, Helvetica Neue, Helvetica, Arial, sans-serif;
    --font-head: Oswald, Helvetica Neue, Helvetica, Arial, sans-serif;
    --background-img: none;
    --background-color: #E30615;
    --text-color: rgba(255, 255, 255, .6);
    --preloader-bg: var(--background-color);
    --question-color: #fff;
    --question-fontweight: 500;
    --answer-color: #fff;
    --answer-gradient: none;
    --answer-bg: transparent;
    --answer-show-bg: rgba(0, 0, 0, .15);
    --answer-bordercolor: #fff;
    --answer-borderactivecolor: #fff;
    --answer-bg-active: #000;
    --answer-bg-activeRGB: 0, 0, 0;
    --answer-bg-active-opacity: 0.7;
    --answer-active: #fff;
    --answer-radius: 0;
    --answer-borderwidth: 2px;
    --answer-borderhoverwidth: 3px;
    --answer-borderselectedwidth: 2px;
    --answer-borderactivewidth: 2px;
    --result-bar-bg: rgba(255, 255, 255, .5);
    --result-bar-color: #2B57A5;
    --answer-fontweight: 300;
  }
}

@keyframes pop {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(.2);
  }

  100% {
    opacity: 1.00;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes fadepop {
  0% {
    opacity: 0;
    transform: scale(.2);
  }

  100% {
    opacity: 1.00;
    transform: scale(1);
  }
}

html {
  height: 100%;
}

body {
  color: var(--text-color);
  margin: 0;
  height: 100%;
}

* {
  box-sizing: border-box;
}

.poll-wrap {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  min-height: 100%;
  background-color: var(--background-color);
  overflow-y: hidden;
}

.swpoll-content {
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  flex: 1 1 auto;
  width: 100%;

  @media only screen and (min-width: 650px) {
    max-width: var(--content-block-width);
    margin: 0 auto;
  }
}

*+.swpoll-content {
  padding: 60px 0;
}

.preloader-enter-active,
.preloader-leave-active {
  transition: all 750ms cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: 250ms;
}

.preloader-enter,
.preloader-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.navheader-enter-active,
.navheader-leave-active {
  transition: all 750ms cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: 250ms;
}

.navheader-enter,
.navheader-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.block-1-enter-active,
.block-1-leave-active,
.block-2-enter-active,
.block-2-leave-active,
.block-3-enter-active,
.block-3-leave-active,
.block-4-enter-active,
.block-4-leave-active,
.block-5-enter-active,
.block-5-leave-active {
  transition: all 750ms cubic-bezier(0.22, 1, 0.36, 1);
}

.block-1-enter-active,
.block-1-leave-active {
  transition-delay: 500ms;
}

.block-2-enter-active,
.block-2-leave-active {
  transition-delay: 600ms;
}

.block-3-enter-active,
.block-3-leave-active {
  transition-delay: 700ms;
}

.block-4-enter-active,
.block-4-leave-active {
  transition-delay: 800ms;
}

.block-5-enter-active,
.block-5-leave-active {
  transition-delay: 1200ms;
}

.block-1-enter,
.block-2-enter,
.block-3-enter,
.block-4-enter,
.block-5-enter {
  opacity: 0;
  transform: scale(0.8);
}

.block-1-enter-to,
.block-2-enter-to,
.block-3-enter-to,
.block-4-enter-to,
.block-5-enter-to {
  opacity: 1;
  transform: scale(1);
}

.hide {
  display: none;
}

.isHorizontal {
  .swpoll-content {
    max-width: inherit;
    padding: 0 5px;
  }

  &.headerEnabled {
    margin-top: 60px;
  }
}

.style-bbl,
.style-veev {

  &.poll-wrap {
    background: none;
    overflow: hidden;
  }

  .swpoll-content {
    position: relative;
    align-items: initial;
    margin: 0;
  }
}

.style-veev {
  --answer-bordercolor: #A3793E;
}

.page-end-marker {
  color: transparent;
  height: 1px;
}

.vote-limit-reached {
  background-color: var(--danger-color);
  border-radius: 25px;
  padding: 4px 16px;
  display: inline-block;
  color: var(--white);
  z-index: 10;
}

.digitaljoy-icon {
  display: flex;
  z-index: 10;
  flex-direction: row;
  justify-content: flex-end;
  padding: 10px;
  position: fixed;
  bottom: 0;
  width: 100%;
}
</style>
